/**
 * Strips HTML tags from a string and truncates it to a specified length
 * @param htmlString - The HTML string to process
 * @param maxLength - Maximum number of characters to keep (default: 200)
 * @returns The processed string without HTML tags and truncated if necessary
 */
export const stripHtmlAndTruncate = (
  htmlString: string,
  maxLength: number = 250
): string => {
  if (!htmlString) {
    return "";
  }

  // Create a temporary DOM element to leverage the browser's HTML parsing
  const tempDiv = document.createElement("div");

  // Add spaces around block elements before parsing to preserve word boundaries
  const preprocessedHtml = htmlString
    .replace(
      /<\/?(div|p|h[1-6]|br|li|article|section|header|footer|main|aside|nav|blockquote|pre)[^>]*>/gi,
      " "
    )
    .replace(/<\/(strong|em|span|b|i|u)[^>]*>/gi, " ");

  tempDiv.innerHTML = preprocessedHtml;

  // Get the text content - browser handles all entity decoding automatically
  const cleanText = tempDiv.textContent || tempDiv.innerText || "";

  // Clean up whitespace
  const normalizedText = cleanText.replace(/\s+/g, " ").trim();

  // Return as-is if within length limit
  if (normalizedText.length <= maxLength) {
    return normalizedText;
  }

  // Find the last space before the maxLength to avoid cutting words
  const truncated = normalizedText.substring(0, maxLength);
  const lastSpaceIndex = truncated.lastIndexOf(" ");

  // If we can break at a word boundary and it's not too far back, do so
  if (lastSpaceIndex > 0 && lastSpaceIndex > maxLength * 0.8) {
    return truncated.substring(0, lastSpaceIndex) + "...";
  }

  return truncated + "...";
};
